/**
 * Opening the hub a push notification came from.
 *
 * A tap means "take me to what this is about", and the person has already said
 * yes by tapping — so this connects rather than waiting to be told, which is the
 * one case where the app does not leave that decision to the Local page.
 *
 * It does not invent a route. The app already owns the question "how do I reach
 * this hub right now", and the answer is a ladder that reads the server's own
 * settings first (`services/hubConnect.ts` → `connectResolvingRoute`): a
 * cloud-only server goes straight to the tunnel, a manual address is dialled
 * first, and otherwise the addresses that answered on this network are tried
 * before the tunnel takes over. That is exactly "try local first, then the
 * cloud" — and it is already the tested path every other connect goes through,
 * so a notification is not a second, subtly different way in.
 *
 * What this module adds is the two things a notification needs and a launcher
 * tile does not: the hub is named by an id rather than held as a card, and there
 * is a PAGE to land on once the session is open.
 */

import router from '../router';
import { getI18n } from '../locales';
import { useAuthStore } from '../stores/auth';
import { useToastStore } from '../stores/toast';
import {
  HUB_OPEN_FAILURES,
  hubOpenFailureSentence,
  resolveHubOpenPlan,
  type HubOpenFailure,
  type Translate,
} from '../utils/hubOpenFallback';
import { findAccountHubRow } from './accountHubs';
import { connectResolvingRoute, type SilentConnectResult } from './hubConnect';
import { hubRoute, type PushTarget } from './pushTarget';
import { profileFor } from './serverProfiles';

/** What happened, so a caller can log it and a test can assert it. */
export type PushOpenOutcome =
  /** The session opened; the app is on `path` (or is already going there). */
  | { ok: true; route: SilentConnectResult['route']; path: string }
  /** Neither door opened. The person was taken to the surface that owns the way in. */
  | { ok: false; failure: HubOpenFailure; path: string };

/**
 * One tap at a time. A person can tap a stack of notifications faster than a
 * LAN probe finishes, and two overlapping connects for the same hub fight over
 * the session (the same reason `connectToHub` carries a monotonic attempt
 * counter). The second tap joins the first rather than starting a race.
 */
let inFlight: Promise<PushOpenOutcome> | null = null;

/**
 * Which refusal to name. The cloud's own word wins when it has one — it is the
 * sharper fact (`remote_access_not_licensed` means the hub is local-only, so the
 * tunnel was never a route to it) — and the LAN route's is the fallback. Both
 * vocabularies are members of `HubOpenFailure`, which is why one value can carry
 * either.
 */
function refusalFor(result: SilentConnectResult): HubOpenFailure {
  for (const candidate of [result.cloudRefusal, result.localRefusal]) {
    if (typeof candidate === 'string' && (HUB_OPEN_FAILURES as readonly string[]).includes(candidate)) {
      return candidate as HubOpenFailure;
    }
  }
  return 'unreachable';
}

/**
 * Connect to the hub a notification came from, then land on the page inside it.
 *
 * Never throws. A tap that cannot be honoured takes the person to the surface
 * that owns the way in (the Local page, sign-in, pairing or the launcher —
 * `utils/hubOpenFallback.ts` decides which) rather than to a dead screen.
 */
export function openHubFromPush(target: PushTarget): Promise<PushOpenOutcome> {
  if (inFlight) return inFlight;

  const run = openOnce(target).finally(() => {
    inFlight = null;
  });
  inFlight = run;
  return run;
}

/**
 * Say why a tap could not be honoured, when the destination will not.
 *
 * Every other surface the plan can pick carries the failure itself: the Local
 * page paints `hub.open.failure.*` next to the hub's own facts, and pairing and
 * sign-in are their own explanation. The launcher is not — it is a list of the
 * device's servers and says nothing about the hub that failed, which
 * `utils/hubOpenFallback.ts` keeps that way on purpose (the surfaces that fail
 * *on* it have their own copy). That silence is defensible when the person was
 * already looking at the thing that failed. It is not when they arrived from a
 * notification about their house and are handed a list of servers: a tap that
 * names a hub and then says nothing reads as "push is broken", which is exactly
 * how this was reported.
 *
 * Never throws: a tap that cannot be explained must still not strand anyone, so
 * a toast that will not render is a log line and nothing more.
 */
function announceFailure(messageKey: string, hubName: string | null, address: string | null): void {
  try {
    const t = getI18n().global.t as unknown as Translate;
    useToastStore().addToast({
      type: 'warning',
      message: hubOpenFailureSentence(messageKey, hubName, address, t),
      duration: 8000,
    });
  } catch (error) {
    console.warn('[PushDeepLink] the reason could not be shown:', error);
  }
}

async function openOnce(target: PushTarget): Promise<PushOpenOutcome> {
  const destination = hubRoute(target.hubId, target.path);

  // The app's two records of this hub: the account's (its row id and the key it
  // pinned) and this device's (the addresses that answered here, and the
  // server's own connect preference). Either id spelling may be what arrived.
  const row = findAccountHubRow(target.hubId);
  const cloudId = row?.id ?? target.hubId;
  const ownHubId = row?.hubId ?? target.hubId;
  const profile = profileFor({ cloudId, hubId: ownHubId });

  // Most recent first, which is how the profile keeps them: the address that
  // answered last is the best guess, and the ladder does not retry the others.
  const lan = profile?.lanAddresses?.[0] ?? null;

  const authStore = useAuthStore();
  const result = await connectResolvingRoute({
    mode: 'cloud',
    hubId: cloudId,
    accountHubId: cloudId,
    ...(lan ? { ip: lan.host, port: lan.port } : {}),
    ...(lan && typeof lan.secure === 'boolean' ? { secure: lan.secure } : {}),
    ...(profile?.name ? { name: profile.name } : {}),
  });

  if (result.ok) {
    console.info('[PushDeepLink] opened the hub from a notification', {
      hub: ownHubId,
      route: result.route ?? 'cloud',
      path: destination,
    });
    await router.push(destination);
    return { ok: true, route: result.route ?? 'cloud', path: destination };
  }

  const failure = refusalFor(result);
  const hubName = profile?.name ?? row?.name ?? null;
  const plan = resolveHubOpenPlan({
    failure,
    accountSignedIn: authStore.isCloudAuthenticated,
    hubName,
    address: lan ? { ip: lan.host, port: lan.port } : null,
  });
  console.warn('[PushDeepLink] could not open the hub from a notification', {
    hub: ownHubId,
    failure,
    surface: plan.surface,
  });

  if (plan.surface === 'launcher') {
    announceFailure(plan.messageKey, hubName, lan ? `${lan.host}:${lan.port}` : null);
  }

  await router.push({ path: plan.route.path, query: plan.route.query });
  return { ok: false, failure, path: destination };
}
